@if (state$ | async; as state) {
  <div class="page-header">
    <div class="page-title">{{ state.order ? `Zamówienie ${state.order.orderNumber}` : 'Nowe zamówienie' }}</div>
  </div>

  @if (state.addNodeDropdown) {
    <add-node-dropdown [openAt]="state.addNodeDropdown.position" (bomItemSelected)="onAddNodeBomItemSelected($event)" (closed)="onAddNodeDropdownClose()"></add-node-dropdown>
  }

  @if (state.contextMenu) {
    <context-menu [items]="contextMenuItems" [openAt]="state.contextMenu.position" (actionSelected)="onContextMenuAction($event)" (closed)="onContextMenuClose()"></context-menu>
  }

  <div class="card" id="data-card">
    <div class="card-header">
      DANE

      @if (state.order; as order) {
        <div class="card-header-toolbar">
          @if (order.confirmedAt) {
            <button class="btn" (click)="unconfirm()" title="Cofa potwierdzenie — zamówienie wraca do statusu Nowe">Cofnij potwierdzenie</button>
          } @else {
            <button class="btn" (click)="confirm()" title="Oznacza zamówienie jako potwierdzone przed rozpoczęciem produkcji">Potwierdź</button>
          }
        </div>
      }
    </div>

    @if (state.order; as order) {
      <div class="field" (dblclick)="startEditing('clientName')" title="Dwuklik edytuje">
        <div class="field-label">Klient</div>
        @if (editingField() === 'clientName') {
          <input #fieldInput class="field-input" [value]="order.clientName"
            (keyup.enter)="saveField('clientName', fieldInput.value, order)" (keyup.escape)="stopEditing()" (blur)="stopEditing()">
        } @else {
          <div class="field-value">{{ order.clientName }}</div>
        }
      </div>

      <div class="field" (dblclick)="startEditing('name')" title="Dwuklik edytuje">
        <div class="field-label">Nazwa</div>
        @if (editingField() === 'name') {
          <input #fieldInput class="field-input" [value]="order.name ?? ''"
            (keyup.enter)="saveField('name', fieldInput.value, order)" (keyup.escape)="stopEditing()" (blur)="stopEditing()">
        } @else {
          <div class="field-value" [class.empty]="!order.name">{{ order.name ?? '—' }}</div>
        }
      </div>

      <div class="field" (dblclick)="startEditing('deadline')" title="Dwuklik edytuje">
        <div class="field-label">Termin</div>
        @if (editingField() === 'deadline') {
          <input #fieldInput class="field-input" type="date" [value]="order.deadline ?? ''"
            (change)="saveField('deadline', fieldInput.value, order)" (keyup.escape)="stopEditing()" (blur)="stopEditing()">
        } @else {
          <div class="field-value text-mono" [class.empty]="!order.deadline">{{ order.deadline ? formatDayMonthYear(order.deadline) : '—' }}</div>
        }
      </div>

      <div class="field">
        <div class="field-label">Potwierdzenie</div>
        @if (order.confirmedAt) {
          <div class="field-value">Potwierdzone <span class="text-mono">{{ formatDayMonthYear(order.confirmedAt.slice(0, 10)) }}</span></div>
        } @else {
          <div class="field-value empty">Niepotwierdzone</div>
        }
      </div>

      <div class="field">
        <div class="field-label">Utworzono</div>
        <div class="field-value text-mono">{{ formatDayMonthYear(order.createdAt.slice(0, 10)) }}</div>
      </div>
    } @else {
      <div class="field">
        <div class="field-label">Klient</div>
        <input #clientNameInput class="field-input" [value]="draft().clientName" placeholder="Nazwa klienta"
          (input)="updateDraft('clientName', clientNameInput.value)" (keyup.enter)="create()">
      </div>

      <div class="field">
        <div class="field-label">Nazwa</div>
        <input #nameInput class="field-input" [value]="draft().name ?? ''" placeholder="Opcjonalna"
          (input)="updateDraft('name', nameInput.value)" (keyup.enter)="create()">
      </div>

      <div class="field">
        <div class="field-label">Termin</div>
        <input #deadlineInput class="field-input" type="date" [value]="draft().deadline ?? ''"
          (change)="updateDraft('deadline', deadlineInput.value)">
      </div>

      <button class="btn" [disabled]="!canCreate()" (click)="create()">Utwórz zamówienie</button>
    }
  </div>

  <div class="card" id="flow-card">
    <div class="card-header">PRZEPŁYW</div>

    <client-order-flow [positions]="state.order?.items ?? []"></client-order-flow>
  </div>

  <div class="card" id="items-card">
    <div class="card-header">
      POZYCJE

      @if (state.order && (hasRemaining(state.order.items) || hasUnshipped(state.order.items))) {
        <div class="card-header-toolbar">
          @if (hasRemaining(state.order.items)) {
            <button class="btn" (click)="createProductionOrder()" title="Jedno zlecenie na pozostałe ilości wszystkich pozycji">Utwórz zlecenie</button>
          }

          @if (hasUnshipped(state.order.items)) {
            @if (creatingShipment()) {
              <input #transportInput class="field-input header-input" placeholder="Transport — Enter tworzy wysyłkę"
                (keyup.enter)="createShipment(transportInput.value)" (keyup.escape)="cancelCreatingShipment()" (blur)="cancelCreatingShipment()">
            } @else {
              <button class="btn" (click)="startCreatingShipment()" title="Jedna wysyłka na ilości do wysłania wszystkich pozycji">Utwórz wysyłkę</button>
            }
          }
        </div>
      }
    </div>

    @if (state.order; as order) {
      <div class="positions">
        @for (item of order.items; track item.id) {
          <div class="position" [class.frozen]="isFrozen(item)">
            <div class="position-index">{{ $index + 1 }}</div>
            <div class="position-head">
              <item-photo size="card" [attachmentId]="item.data.primaryPhotoAttachmentId" [label]="item.data.name"></item-photo>
              <client-order-tree-node [item]="item" [frozen]="isFrozen(item)"></client-order-tree-node>
            </div>

            @if (item.data.clientOrderItemId > 0) {
              <div class="coverage">
                <span>zamówiono <span class="text-mono">{{ formatAmount(item.data.amount, item.data.unit) }}</span></span>
                <span>w zleceniach <span class="text-mono">{{ formatAmount(item.data.allocatedAmount, item.data.unit) }}</span></span>
                <span [class.uncovered]="remainingAmount(item) > 0">pozostało <span class="text-mono">{{ formatAmount(remainingAmount(item), item.data.unit) }}</span></span>
              </div>

              <div class="coverage">
                <span>w wysyłkach <span class="text-mono">{{ formatAmount(item.data.inShipmentsAmount, item.data.unit) }}</span></span>
                <span>wysłano <span class="text-mono">{{ formatAmount(item.data.shippedAmount, item.data.unit) }}</span></span>
                <span>do wysłania <span class="text-mono">{{ formatAmount(unshippedAmount(item), item.data.unit) }}</span></span>

                @if (unshippedAmount(item) > 0) {
                  <button class="btn btn-small" #addToShipmentTrigger title="Dodaje ilość do wysłania do wybranej planowanej wysyłki">Dodaj do wysyłki</button>

                  <dropdown [searchFunc]="searchPlannedShipmentsFor(item)" [searchFormatter]="formatShipment" [trigger]="addToShipmentTrigger"
                    [minSearchLength]="0" header="WYSYŁKA" (itemSelected)="onShipmentSelected(item, $event)"></dropdown>
                }
              </div>

              @for (allocation of item.data.allocations; track allocation.allocationId) {
                <div class="allocation">
                  <a class="allocation-order" [routerLink]="['/orders', allocation.orderId]">{{ allocation.orderNumber }}</a>
                  <span class="allocation-status">{{ allocation.statusLabel }}</span>
                  <span class="allocation-window text-mono">{{ allocation.windowLabel ?? '—' }}</span>
                  <span class="allocation-amount text-mono">{{ formatAmount(allocation.amount, allocation.unit) }}</span>
                  <button class="btn btn-small" (click)="unlink(item, allocation)" title="Zwalnia ilość w zamówieniu; zlecenie pozostaje jako produkcja bez przypisania">Odepnij</button>
                </div>
              }

              @for (line of item.data.shipments; track line.shipmentId) {
                <div class="allocation shipment-line">
                  <a class="allocation-order" [routerLink]="['/shipments', line.shipmentId]">{{ line.shipmentNumber }}</a>
                  <span class="allocation-status">{{ line.statusLabel }}</span>
                  <span class="allocation-window text-mono">{{ line.dateLabel ?? '—' }}</span>
                  <span class="allocation-amount text-mono">{{ formatAmount(line.amount, line.unit) }}</span>
                  @if (line.planned) {
                    <button class="btn btn-small" (click)="removeFromShipment(item, line)" title="Zdejmuje pozycję z wysyłki; ilość wraca do wysłania">Usuń z wysyłki</button>
                  } @else {
                    <span></span>
                  }
                </div>
              }

              @if (isFrozen(item)) {
                <div class="frozen-note">pozycja jest już objęta zleceniem — odepnij, aby edytować</div>
              }
            }
          </div>
        } @empty {
          <div class="positions-empty">Brak pozycji — dodaj pierwszą z katalogu BOM.</div>
        }
      </div>

      <button class="btn" #dropdownTrigger>+ Nowa pozycja</button>

      <dropdown [searchFunc]="searchBomItems" [searchFormatter]="formatBomItem" [trigger]="dropdownTrigger"
        [minSearchLength]="0" [allowCreate]="true" [exactMatch]="isExactBomItemMatch" header="POZYCJA" (itemSelected)="onPositionSelected($event)"></dropdown>
    } @else {
      <div class="positions-empty">Zapisz dane zamówienia, aby dodać pozycje.</div>
    }
  </div>
}
